import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { OneKlicksFileSDK } from "../../../SDK/OneKlicksFileSDK";
import { toast } from "react-toastify";
import { Card, Badge, Form, InputGroup, Modal } from "react-bootstrap";
import { Button, Select, Upload } from "antd";
import { ReloadOutlined, UploadOutlined, DownloadOutlined, DeleteOutlined, FileTextOutlined, PlusOutlined } from "@ant-design/icons";

const { Dragger } = Upload;

const normaliseResponse = (response) => {
    if (!response?.success) {
        return [];
    }
    if (Array.isArray(response?.data)) {
        return response.data;
    }
    if (Array.isArray(response?.data?.items)) {
        return response.data.items;
    }
    return [];
};

const toNumericOrNull = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const Documents = () => {
    const { userData } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadCategory, setUploadCategory] = useState("policies");
    const [uploadDocumentName, setUploadDocumentName] = useState("");

    const companyId = useMemo(
        () => toNumericOrNull(userData?.company_id || userData?.companyId || userData?.company),
        [userData]
    );

    const agentId = useMemo(
        () => toNumericOrNull(userData?.agent_id || userData?.agentId),
        [userData]
    );

    useEffect(() => {
        fetchDocuments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id, companyId, agentId, categoryFilter]);

    const fetchDocuments = async () => {
        if (!userData?.id) {
            setDocuments([]);
            return;
        }

        try {
            setLoading(true);
            const conditions = {};
            
            // Priority 1: Filter by company_id if user belongs to a company
            if (companyId) {
                conditions.company_id = companyId;
            }
            // Priority 2: Filter by agent_id if user is an agent
            else if (agentId) {
                conditions.agent_id = agentId;
            }
            // Priority 3: Filter by user_id for individual users
            else {
                conditions.user_id = userData.id;
            }
            
            if (categoryFilter !== "all") {
                conditions.category = categoryFilter;
            }
            
            console.log("Fetching documents with conditions:", conditions);
            
            const response = await oneKlicks.read(
                "",
                "insurance_documents",
                conditions,
                "created_at DESC",
                "",
                "",
                "0,200"
            );

            const documentRows = normaliseResponse(response);
            setDocuments(documentRows || []);
        } catch (error) {
            console.error("Error fetching documents:", error);
            toast.error("Failed to load documents");
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredDocuments = useMemo(() => {
        let filtered = documents;
        
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter((doc) => {
                return (
                    doc.document_name?.toLowerCase().includes(searchLower) ||
                    doc.file_name?.toLowerCase().includes(searchLower) ||
                    doc.category?.toLowerCase().includes(searchLower) ||
                    doc.description?.toLowerCase().includes(searchLower)
                );
            });
        }
        
        return filtered;
    }, [documents, searchText]);

    const handleFileSelect = (file) => {
        // Validate file type
        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'image/jpeg',
            'image/jpg',
            'image/png',
            'text/plain'
        ];
        
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload PDF, Word, Image, or Text files only');
            return false;
        }

        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return false;
        }

        setUploadFile(file);
        if (!uploadDocumentName.trim()) {
            setUploadDocumentName(file.name);
        }
        return false; // Prevent auto upload
    };

    const handleUpload = async () => {
        if (!uploadFile) {
            toast.error("Please select a file");
            return;
        }

        if (!uploadDocumentName.trim()) {
            toast.error("Please enter a document name");
            return;
        }

        setUploading(true);
        try {
            const root = "insurance/documents";
            const status = "public";
            
            console.log("Uploading file:", uploadFile.name, uploadFile.size);
            const uploadResult = await OneKlicksFileSDK.uploadFile(root, status, uploadFile);
            console.log("Upload result:", uploadResult);
            
            if (uploadResult?.success) {
                // Extract file ID from different possible response structures
                const fileId = uploadResult.id || uploadResult.fileid || uploadResult.fileId || uploadResult.file?.id || uploadResult.file?.fileId;
                
                // Extract file URL or construct it
                let fileUrl = uploadResult.fileUrl || uploadResult.url || uploadResult.file?.fileUrl;
                
                // If we have file info with root and fileName, construct URL (as in InvoicePrint.js)
                if (!fileUrl && uploadResult.file) {
                    if (uploadResult.file.root && uploadResult.file.fileName) {
                        // Construct URL from root and fileName
                        fileUrl = `${uploadResult.file.root}/${uploadResult.file.fileName}`;
                    } else if (uploadResult.file.fileName) {
                        // Use root parameter if file.root is not available
                        fileUrl = `${root}/${uploadResult.file.fileName}`;
                    }
                }
                
                // If we have fileId but no URL, try to get it using getfile API
                if (fileId && !fileUrl) {
                    try {
                        const fileInfo = await OneKlicksFileSDK.getfile(fileId);
                        console.log("File info from getfile:", fileInfo);
                        if (fileInfo?.success && fileInfo?.fileUrl) {
                            fileUrl = fileInfo.fileUrl;
                        }
                    } catch (error) {
                        console.log("Could not fetch file URL, will use fileId:", error);
                    }
                }
                
                console.log("Extracted fileId:", fileId, "fileUrl:", fileUrl);

                // Create document record in database
                const documentData = {
                    user_id: userData?.id,
                    company_id: companyId || null,
                    agent_id: agentId || null,
                    document_name: uploadDocumentName.trim(),
                    file_name: uploadFile.name,
                    file_id: fileId || null,
                    file_url: fileUrl || null,
                    category: uploadCategory,
                    file_size: formatFileSize(uploadFile.size),
                    file_size_bytes: uploadFile.size,
                    created_at: new Date().toISOString().slice(0, 19).replace("T", " "),
                    updated_at: new Date().toISOString().slice(0, 19).replace("T", " ")
                };

                console.log("Creating document record:", documentData);
                const createResponse = await oneKlicks.create("", "insurance_documents", documentData);
                console.log("Create response:", createResponse);
                
                if (createResponse?.success) {
                    toast.success("Document uploaded and saved successfully");
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadDocumentName("");
                    setUploadCategory("policies");
                    // Refresh the documents list
                    await fetchDocuments();
                } else {
                    const errorMsg = createResponse?.error || createResponse?.message || "Unknown error";
                    console.error("Failed to save document record:", errorMsg);
                    toast.error(`Document uploaded but failed to save: ${errorMsg}`);
                }
            } else {
                const errorMsg = uploadResult?.error || uploadResult?.message || "Upload failed";
                console.error("Upload failed:", errorMsg);
                toast.error(`Failed to upload file: ${errorMsg}`);
            }
        } catch (error) {
            console.error("Error uploading document:", error);
            toast.error(`Failed to upload document: ${error.message || 'Unknown error'}`);
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (document) => {
        try {
            let fileUrl = document.file_url || document.file_name;
            
            // If we have file_id but no URL, try to get it
            if (!fileUrl && document.file_id) {
                try {
                    const fileInfo = await OneKlicksFileSDK.getfile(document.file_id);
                    if (fileInfo?.success && fileInfo?.fileUrl) {
                        fileUrl = fileInfo.fileUrl;
                    }
                } catch (error) {
                    console.log("Could not fetch file URL:", error);
                }
            }
            
            if (!fileUrl) {
                toast.error("File URL not available");
                return;
            }

            // Construct full URL if needed
            let fullUrl = fileUrl;
            if (!fileUrl.startsWith('http')) {
                // If it's a relative path, prepend API base URL
                if (fileUrl.startsWith('/')) {
                    fullUrl = `${process.env.REACT_APP_API_BASE_URL || ''}${fileUrl}`;
                } else {
                    // If it's just a filename or path, construct full URL
                    fullUrl = `${process.env.REACT_APP_API_BASE_URL || ''}/files/${fileUrl}`;
                }
            }
            
            console.log("Downloading file:", fullUrl);
            window.open(fullUrl, '_blank');
        } catch (error) {
            console.error("Error downloading document:", error);
            toast.error("Failed to download document");
        }
    };

    const handleDelete = async (documentId) => {
        if (!window.confirm("Are you sure you want to delete this document?")) {
            return;
        }

        try {
            const deleteResponse = await oneKlicks.delete("", "insurance_documents", { id: documentId });
            
            if (deleteResponse?.success) {
                toast.success("Document deleted successfully");
                fetchDocuments();
            } else {
                toast.error("Failed to delete document");
            }
        } catch (error) {
            console.error("Error deleting document:", error);
            toast.error("Failed to delete document");
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return "0 B";
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const getCategoryBadge = (category) => {
        const badges = {
            policies: { bg: 'info', label: 'Policies' },
            claims: { bg: 'warning', label: 'Claims' },
            identification: { bg: 'success', label: 'Identification' },
            other: { bg: 'secondary', label: 'Other' }
        };
        const badge = badges[category] || badges.other;
        return <Badge bg={badge.bg} style={{ borderRadius: "5px", textTransform: 'capitalize' }}>{badge.label}</Badge>;
    };

    return (
        <div className="subbody" style={{ width: "100%", padding: "2% 4%" }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    {/* Header */}
                    <div className="card mb-5" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Documents Management</h5>
                                    <Badge bg="info" style={{ borderRadius: "5px" }}>Documents</Badge>
                                    <Badge bg="secondary" style={{ borderRadius: "5px" }}>{documents.length} Files</Badge>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <Button 
                                        className="btn-outline-primary" 
                                        onClick={fetchDocuments}
                                        disabled={loading}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <ReloadOutlined /> Refresh
                                    </Button>
                                    <Button 
                                        className="btn-primary" 
                                        onClick={() => setShowUploadModal(true)}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <PlusOutlined /> Upload Document
                                    </Button>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3">
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Search
                                    </label>
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Search documents..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ height: '40px', borderRadius: "5px" }}
                                        />
                                    </InputGroup>
                                </div>
                                <div style={{ minWidth: '180px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Category
                                    </label>
                                    <Select
                                        value={categoryFilter}
                                        onChange={(value) => setCategoryFilter(value)}
                                        style={{ width: '100%' }}
                                        size="large"
                                        options={[
                                            { value: 'all', label: 'All Categories' },
                                            { value: 'policies', label: 'Policies' },
                                            { value: 'claims', label: 'Claims' },
                                            { value: 'identification', label: 'Identification' },
                                            { value: 'other', label: 'Other' },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Documents Table */}
                    <div className="card" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            {loading ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : filteredDocuments.length > 0 ? (
                                <div className="table-responsive">
                                    <table hover size="sm" className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px' }}>Icon</th>
                                                <th>Document Name</th>
                                                <th>Category</th>
                                                <th>Upload Date</th>
                                                <th>Size</th>
                                                <th style={{ width: '150px' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredDocuments.map((doc) => (
                                                <tr key={doc.id}>
                                                    <td data-label="Icon">
                                                        <div style={{ 
                                                            width: '40px', 
                                                            height: '40px', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: '#e3f2fd', 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center',
                                                            color: '#2196f3'
                                                        }}>
                                                            <FileTextOutlined style={{ fontSize: '18px' }} />
                                                        </div>
                                                    </td>
                                                    <td data-label="Document Name">
                                                        <div style={{ fontWeight: '500', color: '#333' }}>
                                                            {doc.document_name || doc.file_name || "Untitled Document"}
                                                        </div>
                                                        {doc.description && (
                                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                                {doc.description}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td data-label="Category">
                                                        {getCategoryBadge(doc.category)}
                                                    </td>
                                                    <td data-label="Upload Date" style={{ fontSize: '13px', color: '#666' }}>
                                                        {formatDate(doc.created_at)}
                                                    </td>
                                                    <td data-label="Size" style={{ fontSize: '13px', color: '#666' }}>
                                                        {doc.file_size || formatFileSize(doc.file_size_bytes) || "N/A"}
                                                    </td>
                                                    <td data-label="Action">
                                                        <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                            <Button 
                                                                href="#" 
                                                                className="dc-update-servicesss fit-content btn-info" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleDownload(doc);
                                                                }}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                <DownloadOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                Download
                                                            </Button>
                                                            <Button 
                                                                href="#" 
                                                                className="dc-update-servicesss fit-content btn-danger" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleDelete(doc.id);
                                                                }}
                                                                style={{ borderRadius: "5px" }}
                                                            >
                                                                <DeleteOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>No documents found</em>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Upload Modal */}
            <Modal 
                show={showUploadModal} 
                onHide={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadDocumentName("");
                    setUploadCategory("policies");
                }}
                centered
                style={{ borderRadius: "5px" }}
            >
                <Modal.Header closeButton style={{ borderRadius: "5px 5px 0 0" }}>
                    <Modal.Title>Upload Document</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <div className="mb-3">
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                            Document Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Form.Control
                            type="text"
                            placeholder="Enter document name"
                            value={uploadDocumentName}
                            onChange={(e) => setUploadDocumentName(e.target.value)}
                            style={{ borderRadius: "5px" }}
                        />
                    </div>
                    <div className="mb-3">
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                            Category <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Select
                            value={uploadCategory}
                            onChange={(value) => setUploadCategory(value)}
                            style={{ width: '100%' }}
                            size="large"
                            options={[
                                { value: 'policies', label: 'Policies' },
                                { value: 'claims', label: 'Claims' },
                                { value: 'identification', label: 'Identification' },
                                { value: 'other', label: 'Other' },
                            ]}
                        />
                    </div>
                    <div className="mb-3">
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                            Select File <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dragger
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                            beforeUpload={handleFileSelect}
                            fileList={uploadFile ? [{
                                uid: '-1',
                                name: uploadFile.name,
                                status: 'done',
                            }] : []}
                            style={{ borderRadius: "5px" }}
                        >
                            <p className="ant-upload-drag-icon">
                                <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                            </p>
                            <p className="ant-upload-text">Click or drag file to this area to upload</p>
                            <p className="ant-upload-hint">
                                Support for PDF, Word, Images, and Text files. Max file size: 10MB
                            </p>
                        </Dragger>
                        {uploadFile && (
                            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: "5px" }}>
                                <strong>Selected:</strong> {uploadFile.name} ({formatFileSize(uploadFile.size)})
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ borderRadius: "0 0 5px 5px" }}>
                    <Button 
                        onClick={() => {
                            setShowUploadModal(false);
                            setUploadFile(null);
                            setUploadDocumentName("");
                            setUploadCategory("policies");
                        }}
                        style={{ borderRadius: "5px" }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleUpload}
                        disabled={uploading || !uploadFile || !uploadDocumentName.trim()}
                        loading={uploading}
                        style={{ borderRadius: "5px" }}
                    >
                        {uploading ? 'Uploading...' : 'Upload Document'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Documents;
